// src/components/pages/CompanyJobs.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import storageService from '../../services/storageService';
import './CompanyJobs.css';

const CompanyJobs = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompanyData();
  }, [companyId]);

  const loadCompanyData = () => {
    const companyData = storageService.getCompany(companyId);
    const companyJobs = storageService.getJobsByCompany(companyId);
    
    // Add candidate counts to each job
    const jobsWithCandidateCounts = companyJobs.map(job => {
      const placements = storageService.getPlacementsByJob(job.id);
      return {
        ...job,
        candidateCount: placements.length,
        activeCandidates: placements.filter(p => 
          p.status === 'Resume Sent' || p.status === 'Interview in Progress'
        ).length
      };
    });
    
    setCompany(companyData);
    setJobs(jobsWithCandidateCounts);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading company jobs...</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="error-state">
        <h2>Company not found</h2>
        <Link to="/companies" className="btn-primary">
          Back to Companies
        </Link>
      </div>
    );
  }

  return (
    <div className="company-jobs-page">
      {/* Company Header */}
      <div className="company-header-section">
        <div className="company-header-top">
          <Link to="/companies" className="back-link">
            ← Back to Companies
          </Link>
          <Link 
            to={`/jobs/new?companyId=${companyId}`}
            className="btn-primary"
          >
            + Add Job
          </Link>
        </div>
        
        <div className="company-detail-card">
          <div className="company-logo-large">
            {company.name.charAt(0)}
          </div>
          <div className="company-info">
            <h1>{company.name}</h1>
            <div className="company-meta">
              <span className="company-industry">{company.industry}</span>
              <span className="company-status">{company.status}</span>
            </div>
            <div className="company-contact">
              <p><strong>Contact:</strong> {company.contactPerson}</p>
              <p><strong>Email:</strong> {company.email}</p>
              <p><strong>Phone:</strong> {company.phone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="company-stats">
        <div className="stat-card">
          <div className="stat-icon">💼</div>
          <div className="stat-content">
            <h3>{jobs.length}</h3>
            <p>Total Jobs</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{jobs.filter(j => j.status === 'Open').length}</h3>
            <p>Open Positions</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👤</div>
          <div className="stat-content">
            <h3>{jobs.reduce((acc, job) => acc + job.candidateCount, 0)}</h3>
            <p>Total Candidates</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>{jobs.reduce((acc, job) => acc + job.activeCandidates, 0)}</h3>
            <p>Active Applications</p>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="section-container">
        <div className="section-header">
          <h2>Job Requirements ({jobs.length})</h2>
        </div>
        
        <div className="jobs-table-container">
          {jobs.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Requirements</th>
                  <th>Experience</th>
                  <th>Salary</th>
                  <th>Candidates</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id}>
                    <td>
                      <div className="job-title-cell">
                        <div className="job-icon">💼</div>
                        <div>
                          <Link to={`/job/${job.id}/candidates`} className="job-title-link">
                            {job.title}
                          </Link>
                          <div className="job-meta">
                            <span className="job-location">{job.location || 'Remote'}</span>
                            <span className="job-type">{job.jobType}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="requirements-cell">
                        {job.requirements?.split(',').slice(0, 2).map((req, idx) => (
                          <span key={idx} className="requirement-tag">{req.trim()}</span>
                        ))}
                        {job.requirements?.split(',').length > 2 && (
                          <span className="more-tag">+{job.requirements.split(',').length - 2} more</span>
                        )}
                      </div>
                    </td>
                    <td>{job.experience || 'Not specified'}</td>
                    <td className="salary-cell">{job.salary || 'Negotiable'}</td>
                    <td>
                      <div className="candidate-stats">
                        <div className="candidate-count">
                          <span className="count-number">{job.candidateCount}</span>
                          <span className="count-label">applied</span>
                        </div>
                        {job.activeCandidates > 0 && (
                          <div className="active-candidates">
                            ({job.activeCandidates} active)
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${job.status === 'Open' ? 'open' : 'closed'}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/job/${job.id}/candidates`}
                          className="btn-view-candidates"
                        >
                          View Candidates
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">💼</div>
              <h3>No jobs found for this company</h3>
              <p>Add a job requirement to get started</p>
              <Link 
                to={`/jobs/new?companyId=${companyId}`}
                className="btn-primary"
              >
                + Add Job
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyJobs;